import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { Endpoints } from 'src/environments/endpoints';
import { IResponse } from '../model/http/brands.model';
import { ICarFullResponse } from '../model/http/cars.model';
import { Ifilters, IKilometer } from '../model/http/filter.model';
import { IImagesJato } from '../model/http/images.model';
import { IAllSpecs } from '../model/http/specs.model';
import { ICities } from '../pages/buro/interfaces/city-model';
import { Countries } from '../pages/buro/interfaces/countries';
import { IZipCode } from '../pages/buro/interfaces/zip-model';
import { ConsumeService } from './consume.service';
import { ICampaignsTo, ICatalogsRes } from '../pages/buro/interfaces/catalogos.model';
import { IStates } from '../pages/buro/interfaces/states.model';

@Injectable({
  providedIn: 'root',
})
export class CatalogsService {
  constructor(private consume: ConsumeService) { }

  getBrands(): Observable<IResponse> {
    return this.consume.httpGet(`${Endpoints.catalogs.base}/brand`);
  }

  getSubBrands(param: string): Observable<IResponse> {
    return this.consume.httpGet(`${Endpoints.catalogs.base}/sub-brand${param}`);
  }

  getYearsSubBrands(param: string): Observable<IResponse> {
    return this.consume.httpGet(`${Endpoints.catalogsV3.base}/years-sub-brand${param}`);
  }

  getVersion(param: string): Observable<IResponse> {
    return this.consume.httpGet(`${Endpoints.catalogs.base}/version${param}`);
  }

  getTopCars(param: string = ''): Observable<IResponse> {
    param = param.replace('?top-cars', '');
    return this.consume.httpGet(`${Endpoints.catalogs.base}/top-cars${param}`).pipe(
      map((resp: any) => {
        return {
          data: resp.data.cars,
          page: resp.data.page,
          per_page: resp.data.per_page,
          total: resp.data.total,
          total_pages: resp.data.total_pages,
          status: 200,
          message: resp.message
        }
      })
    );
  }

  getFilters(): Observable<Ifilters> | Observable<any> {
    return this.consume.httpGet(`${Endpoints.catalogsV2.base}/filters`);
  }

  getSearchCars(param: string): Observable<ICarFullResponse> {
    return this.consume.httpGet(`${Endpoints.catalogs.base}/cars?${param}`);
  }

  getImageById(param: string): Observable<IImagesJato> {
    return this.consume.httpGet(
      `${Endpoints.catalogs.imgesById}?uid_jato=${param}`
    ).pipe(
      map((resp: any) => resp.data)
    );
  }

  getStates(): Observable<IStates> {
    return this.consume.httpGet<IStates>(`${Endpoints.catalogs.base}/states`).pipe(
      map((resp) => resp)
    );
  }

  getCities(stateId: number): Observable<ICities> {
    return this.consume
      .httpGet<ICities>(`${Endpoints.catalogs.base}/cities/${stateId}`)
      .pipe(
        map((resp: any) => resp.data.data),
        catchError((err) => {
          console.log(err);
          return of({ cities: [] });
        })
      );
  }

  getZipCode(zipCode: string): Observable<IZipCode> {
    return this.consume
      .httpGet<IZipCode>(`${Endpoints.catalogs.base}/zip-code/${zipCode}`)
      .pipe(
        map((resp: any) => resp.data.data),
        catchError((err) => {
          console.log(err);
          return of({ resultados: [] });
        })
      );
  }

  getCountries(): Observable<Countries | any> {
    return this.consume.httpGet<Countries>(`${Endpoints.catalogs.base}/countries`)
      .pipe(map((res: any) => res.data.data)
      );
  }

  getRangeKm(): Observable<IKilometer> {
    return this.consume.httpGet(`${Endpoints.catalogs.base}/catalogs?table=ranges&page=1&size=20`).pipe(
      map((resp: any) => resp.data)
    );
  }

  getCreditRangeKm(): Observable<IKilometer> {
    return this.consume.httpGet(`${Endpoints.catalogs.base}/catalogs?table=creditRanges`).pipe(
      map((resp: any) => resp.data)
    );
  }

  getSpecsById(param: string): Observable<IAllSpecs> {
    return this.consume.httpGet(`${Endpoints.catalogs.specsById}?uid_jato=${param}`).pipe(
      map((resp: any) => resp.data.data)
    );
  }

  getQuoteCarById(id: string): Observable<any> {
    return this.consume.httpGet(`${Endpoints.catalogs.base}/cars/${id}`);
  }

  validateCodePromo(code: string): Observable<any> {
    return this.consume.httpGet(`${Endpoints.catalogsV2.base}/validateCode/${code}`);
  }

  validateCreditCodePromo(code: string): Observable<any> {
    return this.consume.httpGet(`${Endpoints.catalogsV3.base}/validateCode/credit/${code}`);
  }

  getSuggestions(param: string): Observable<any> {
    let suggestions: any[] = [];
    return this.consume.httpGet(`${Endpoints.catalogsV3.base}/cars-suggestion${param}`).pipe(
      map((resp: any) => {
        if (resp.data.length > 0) {
          let objeto = resp.data;
          objeto.forEach((p: any) => {
            if (suggestions.findIndex(pd => pd.subbrand === p.subbrand) === -1) {
              suggestions.push(p);
            }
          });
        }
        return suggestions;
      })
    )
  }

  /**
   * Obtiene la tabla de tipos de cliente para obtener la renta interina.
   * @return {*}  {Observable<ICatalogsRes>}
   * @memberof CatalogsService
   * @author victor.mendez
   */
  getCatalogTypeClient(): Observable<ICatalogsRes> {
    return this.consume.httpGet(`${Endpoints.catalogs.catalog}?table=typeClients&page=1&size=10`).pipe(map((resp: any) => resp.data));
  }

  /**
   * Obtiene lo parametros de configuración del BackOffice de la campaña ford.
   * Tacla cs_spec_campaign
   * @return {*}  {Observable<ICampaignsTo>}
   * @memberof CatalogsService
   * @author victor.mendez
   * @date 2025-04-24
   */
  getParamsFord(idLeasetech: number): Observable<ICampaignsTo> {
    return this.consume.httpGet(`${Endpoints.cotizadorV3.base}/paramsCampaign/${idLeasetech}`).pipe(
      map((resp: any) => resp )
    );
  }
}
